import { ApolloError, FetchResult } from "@apollo/client";
import { useCallback } from "react";

import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import {
  Answers,
  ConfirmationAnswer,
  Detail,
  PSRTemporaryNeedsCodes,
} from "../../context/vulnerability/VulnerabilityContext";
import {
  CustomerSupportCustomerInstigatorOrigin,
  CustomerSupportSubmissionType,
  useSubmitAssessmentMutation,
  CustomerSupportVulnerabilityAssessmentVersion,
  CustomerSupportSubmission,
  MultiChoice,
  CustomerSupportVulnerabilityCircumstanceDailyActivityInput,
  CustomerSupportVulnerabilityPsrNeed,
  CustomerSupportVulnerabilityNeedType,
  SubmitAssessmentMutation,
} from "../../generated/customer/schema.graphql";
import Sentry from "../../sentry";
import useTrackVulnerabilityJourney from "../../middleware/analytics/hooks/useTrackVulnerabilityJourney";
import { noPsrServicesRequired } from "../../pages/utils";
import { isBrowserSupported } from "../../components/UnsupportedBrowser";
import { useAuthContext } from "../../context/auth/useAuthContext";

interface UseSubmitAssessmentResponse {
  submitAssessment: () => Promise<FetchResult<SubmitAssessmentMutation> | undefined>;
  loading: boolean;
  error?: ApolloError;
}

export const buildSubmission = (
  answers: Answers
): CustomerSupportSubmission => {
  return {
    submissionType: CustomerSupportSubmissionType.AssessmentCompleted,
    version: CustomerSupportVulnerabilityAssessmentVersion.V2,
    ...(answers.children &&
      !answers.children.noneOfTheAbove && {
        children: {
          pregnant: mapChoice(answers.children.pregnant),
          childrenUnder2: mapChoice(answers.children.childrenUnder2),
          childrenBetween2And4: mapChoice(
            answers.children.childrenBetween2And4
          ),
          childrenBetween5And15: mapChoice(
            answers.children.childrenBetween5And15
          ),
        },
      }),
    ...(answers.hearing &&
      answers.hearing === ConfirmationAnswer.Yes && {
        hearing: {
          deafOrHardOfHearing: mapConfirmationAnswer(answers.hearing),
        },
      }),
    ...(answers.sight &&
      answers.sightDetail && { sight: mapCircumstance(answers.sightDetail) }),
    ...(answers.age &&
      answers.ageDetail && {
        age: {
          ...mapCircumstance(answers.ageDetail),
          householdHasLiveInCarer: mapConfirmationAnswer(answers.ageCare),
        },
      }),
    ...(answers.medicalEquipment &&
      answers.medicalEquipmentDetail && {
        medicalEquipment: mapCircumstance(answers.medicalEquipmentDetail),
      }),
    ...(answers.miscellaneous && {
      miscellaneous: mapCircumstance(answers.miscellaneous),
    }),
    ...(answers.chronicIllness &&
      answers.chronicIllnessDetail && {
        chronicIllness: mapCircumstance(answers.chronicIllnessDetail),
      }),
    ...(answers.financial &&
      answers.financialDetail && {
        financial: mapCircumstance(answers.financialDetail),
      }),
    ...(answers.dailyActivity && { dailyActivity: mapDailyActivity(answers) }),
    ...(answers.psr && {
      psr: {
        psrOutcome: {
          eligible: answers.psr.psrOutcome.eligible,
          optedIn: answers.psr.psrOutcome.optedIn,
        },
        ...(answers.psr.psrTemporaryNeedsCodes && {
          psrNeeds: mapTemporaryNeeds(answers.psr.psrTemporaryNeedsCodes),
        }),
        ...(answers.psr.psrServices && {
          psrServices: answers.psr.psrServices,
        }),
      },
    }),
  };
};

export const useSubmitAssessmentCustomer = (): UseSubmitAssessmentResponse => {
  const { answers } = useVulnerabilityContext();
  const { trackVulnerabilityJourneyFinished } = useTrackVulnerabilityJourney();
  const { instigator } = useAuthContext();

  const customerAccountNo = instigator.accountNumber;
  const customerAccountID = instigator?.accountID;

  const [submitAssessmentMutation, { loading, error }] =
    useSubmitAssessmentMutation({
      onError: (e: ApolloError) => {
        if (isBrowserSupported()) {
          Sentry.getCurrentScope()
            .setTransactionName("useSubmitAssessmentCustomer")
            .setTag("accountID", customerAccountID)
            .setTag("accountNumber", customerAccountNo)
            .captureException(e);
        }
      },
    });

  const submitAssessment = useCallback(async () => {
    try {
      const response = await submitAssessmentMutation({
        variables: {
          request: {
            instigator: {
              customerOrigin: CustomerSupportCustomerInstigatorOrigin.Web,
            },
            submission: buildSubmission(answers),
          },
        },
      });
      trackVulnerabilityJourneyFinished({
        eligible_for_psr: answers.psr?.psrOutcome.eligible,
        opted_in_for_psr: answers.psr?.psrOutcome.optedIn,
        bill_in_audio_selected: answers.psr?.psrServices?.billInAudio,
        bill_in_braille_selected: answers.psr?.psrServices?.billInBraille,
        bill_in_large_print_selected:
          answers.psr?.psrServices?.billInLargePrint,
        quarterly_meter_read_selected:
          answers.psr?.psrServices?.quarterlyMeterRead,
        pass_phrase_selected: answers.psr?.psrServices?.passPhrase
          ? true
          : false,
        send_knob_turner_selected: answers.psr?.psrServices?.sendKnobTurner,
        no_services_required: noPsrServicesRequired(answers.psr?.psrServices),
      });
      return response;
    } catch (err) {
      if (!(err instanceof ApolloError)) {
        throw err;
      }
    }
  }, [answers, submitAssessmentMutation]);

  return { submitAssessment, loading, error };
};

export const mapConfirmationAnswer = (
  confirmationAns: ConfirmationAnswer | undefined
): MultiChoice => {
  switch (confirmationAns) {
    case "Yes":
      return MultiChoice.True;
    case "No":
      return MultiChoice.False;
    default:
      return MultiChoice.Unknown;
  }
};

type MappedAnswer<T> = Required<{
  [P in keyof T]: MultiChoice;
}>;

const mapChoice = (choice: boolean): MultiChoice => {
  switch (choice) {
    case true:
      return MultiChoice.True;
    case false:
    default:
      return MultiChoice.False;
  }
};

const mapTemporaryNeeds = (
  need: PSRTemporaryNeedsCodes
): CustomerSupportVulnerabilityPsrNeed[] | undefined => {
  const temporaryNeeds: CustomerSupportVulnerabilityPsrNeed[] = [];

  if (need.bereavementDate) {
    temporaryNeeds.push({
      needType:
        CustomerSupportVulnerabilityNeedType.CustomerSupportVulnerabilityNeedBereavment,
      needBereavment: need.bereavementDate.toISOString().split("T")[0],
    });
  }

  if (need.youngestChildAgeDate) {
    temporaryNeeds.push({
      needType:
        CustomerSupportVulnerabilityNeedType.CustomerSupportVulnerabilityNeedYoungestChild,
      needYoungestChild: need.youngestChildAgeDate.toISOString().split("T")[0],
    });
  }

  if (need.livingAloneDate) {
    temporaryNeeds.push({
      needType:
        CustomerSupportVulnerabilityNeedType.CustomerSupportVulnerabilityNeedLivingAloneUnder18,
      needLivingAloneUnder18: need.livingAloneDate.toISOString().split("T")[0],
    });
  }

  if (need.hospitalDate) {
    temporaryNeeds.push({
      needType:
        CustomerSupportVulnerabilityNeedType.CustomerSupportVulnerabilityNeedBeenInHospital,
      needBeenInHospital: need.hospitalDate.toISOString().split("T")[0],
    });
  }

  return temporaryNeeds;
};

export const mapCircumstance = <T extends Detail>(
  circumstance: Detail
): MappedAnswer<T> => {
  return Object.fromEntries(
    Object.entries(circumstance).map(([key, value]) => [key, mapChoice(value)])
  ) as MappedAnswer<T>;
};

export const mapDailyActivity = (
  ans: Answers
): CustomerSupportVulnerabilityCircumstanceDailyActivityInput | undefined => {
  if (ans.dailyActivity) {
    return {
      difficultyWithEssentialActivities: mapConfirmationAnswer(
        ans.dailyActivity
      ),
      difficultyWithEssentialActivitiesDetails:
        ans.dailyActivity === ConfirmationAnswer.Yes
          ? ans.dailyActivityDetail
          : undefined,
      supportOnDailyBasis: mapConfirmationAnswer(ans.dailySupport),
      supportOnDailyBasisDetails:
        ans.dailySupport === ConfirmationAnswer.Yes
          ? ans.dailySupportDetail
          : undefined,
    };
  }
};
